/**
 * Shared itinerary pricing utility.
 *
 * Single source of truth for converting a per-pax option price into a fully
 * costed quote (discount → TCS → total → advance) for a given pax mode.
 *
 * Imported by BOTH the on-screen pricing section and the Playwright-rendered
 * PDF view (same React component, `?print=1`), so the numbers always match.
 *
 * Correction 1 — discount is AGENT-CONTROLLED via `itinerary.discount_percentage`
 *   (a whole number, e.g. 10 = 10%). PAX_CONFIG no longer carries discounts.
 * Correction 2 — room count is AGENT-CONTROLLED on each accommodation entry;
 *   PAX_CONFIG no longer carries rooms and calc_price no longer returns rooms.
 */

// Pax presets: number of travellers + a display label. No discount, no rooms.
export const PAX_CONFIG = {
  person: { n: 1, label: 'per person' },
  couple: { n: 2, label: 'per couple (2 pax)' },
  family: { n: 4, label: 'per family' },
  group: { n: 8, label: 'per group (8 pax)' },
};

const DEFAULT_ADVANCE_PERCENTAGE = 0.25;
const TCS_RATE = 0.02;

// A1 — a pricing option's price can be entered on different bases. Everything
// is normalised to a per-person figure FIRST, then scaled by the pax count, so
// a "₹1,87,000 per couple" never gets doubled to ₹3,74,000 in couple mode.
export const PRICE_BASES = ['per_person', 'per_couple', 'total_package'];

export function normalizeToPerPerson(price, basis, paxN) {
  if (basis === 'per_couple') return price / 2;
  if (basis === 'total_package') return paxN > 0 ? price / paxN : price;
  return price; // 'per_person' (default)
}

/**
 * Compute the priced quote for one pricing option + pax mode.
 *
 * @param {object} itinerary    - itinerary doc; reads `pricingOptions[0]`
 *                                 (`price` + `price_basis`), `discount_percentage`,
 *                                 `discount_label`, `advance_percentage`.
 *                                 (1b — collapsed from per-option to a single price.)
 * @param {string} paxMode      - one of PAX_CONFIG keys (person/couple/family/group).
 * @returns {{option_price:number, price_basis:string, per_person:number, pax:number,
 *            base:number, discount_amount:number, discount_pct:number,
 *            discount_label:(string|null), tcs:number, total:number,
 *            advance:number, label:string}}
 */
export function calc_price(itinerary, paxMode) {
  const cfg = PAX_CONFIG[paxMode] || PAX_CONFIG.couple;

  const options = (itinerary && itinerary.pricingOptions) || [];
  const opt = options[0] || {};
  const option_price = Number(opt.price) || 0;
  const price_basis = opt.price_basis || 'per_person';

  // A1 — normalise to per-person, THEN scale by the selected pax count.
  const per_person = normalizeToPerPerson(option_price, price_basis, cfg.n);
  const base = per_person * cfg.n;

  // Agent-controlled discount (whole-number percent). Null/undefined → 0.
  const discount_pct =
    itinerary && itinerary.discount_percentage != null
      ? Number(itinerary.discount_percentage) || 0
      : 0;
  const discount_label = (itinerary && itinerary.discount_label) || null;

  const discounted = Math.round(base * (1 - discount_pct / 100));
  const discount_amount = base - discounted;

  // A2 — TCS applies only to international tours.
  const apply_tcs =
    itinerary && itinerary.apply_tcs != null
      ? !!itinerary.apply_tcs
      : (itinerary && itinerary.is_international != null ? !!itinerary.is_international : true);
  const tcs = apply_tcs ? Math.round(discounted * TCS_RATE) : 0;

  // GST is AGENT-INTERNAL (never shown to the client). Three models:
  //   none | package (5% of discounted) | service_fee (18% of the agent fee).
  const service_fee = Number(itinerary && itinerary.service_fee) || 0;
  let gst_model = (itinerary && itinerary.gst_model) || '';
  if (!gst_model) gst_model = (itinerary && itinerary.apply_gst && service_fee) ? 'service_fee' : 'none';
  let gst_amount = 0;
  if (gst_model === 'package') gst_amount = Math.round(discounted * 0.05);
  else if (gst_model === 'service_fee' && service_fee) gst_amount = Math.round(service_fee * 0.18);

  const total = discounted + tcs + gst_amount;

  const advancePct =
    itinerary && itinerary.advance_percentage != null
      ? itinerary.advance_percentage
      : DEFAULT_ADVANCE_PERCENTAGE;
  const advance = Math.round(total * advancePct);

  return {
    option_price,
    price_basis,
    per_person,
    pax: cfg.n,
    base,
    discounted,
    discount_amount,
    discount_pct,
    discount_label,
    apply_tcs,
    tcs,
    gst_model,
    service_fee,
    gst_amount,
    total,
    advance,
    label: cfg.label,
  };
}
